import React, { useState, useMemo, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../Store/actions";
import config from "../../config";
import {
    Button,
    Grid,
    Checkbox,
    Divider,
    DialogActions,
    FormControl,
    FormControlLabel,
    InputLabel,
    Input,
    Select,
    MenuItem,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent
} from "@mui/material/";
import { strings } from "../../common/i18n";
import { logToSentry } from "../../config/Sentry/";

export default ({ onClose }) => {
    const fileInputRef = useRef(null);
    const [fileData, setFileData] = useState({});
    const [delta, setDelta] = useState(true);
    const [csvBoxOpen, setCsvBoxOpen] = useState(false);
    const [uploading, setUploading] = useState(false);

    const { userOrganisations, typesList, auth } = useSelector((state) => state);
    const { fileTypes } = typesList;
    const { identityId } = auth;

    const dispatch = useDispatch();

    /** On filetypes */
    const filetypes = useMemo(() => {
        return fileTypes.data.filter(
            (item) => item.organisationId === userOrganisations.item.id
        );
    }, [fileTypes]);

    /** Handle CSVBox postMessage events */
    useEffect(() => {
        const handleMessage = async (event) => {
            // Verify origin for security
            try{
                if (!event.origin || !event.origin.includes('csvbox.io')) {
                    return;
                }
            } catch (error) {
            if (!event.data) {
                logToSentry("Received message without data from CSVBox:", { event });
                return;
            }
            
            console.log("Received message from CSVBox:", event.data);
            
            // CSVBox sends data when upload is complete
            if (!event.data.success) return;
                try {
                    const submissionData = {
                        name: event.data.sheet_name || `upload_${Date.now()}.csv`,
                        sheet_id: event.data.sheet_id,
                        row_success: event.data.row_success,
                        file_type_id: fileData.file_type_id,
                        isDelta: delta
                    };
                    
                    console.log("Submitting to backend:", submissionData);
                    
                    await dispatch(actions.dataCheck.create.postRequest(submissionData));
                    await dispatch(actions.dataCheck.create.getRequest());
                    
                    dispatch(
                        actions.toast.create.success(
                            `Successfully uploaded ${event.data.row_success} rows`
                        )
                    );
                    dispatch(actions.dataCheck.create.clear());
                    setFileData({});
                    setCsvBoxOpen(false);
                    onClose && onClose();
                    
                } catch (error) {
                    console.error("Error submitting data:", error);
                    logToSentry("On CSVBox data submission:", { error });
                    dispatch(
                        actions.toast.create.error(
                            strings("errors.onDataCheckfileUploadFailure")
                        )
                    );
                }
            }
        };
        
        window.addEventListener('message', handleMessage);
        
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [fileData.file_type_id, delta, dispatch, onClose]);

    /** On dialog Close */
    const handleClose = () => {
        setFileData({});
        setCsvBoxOpen(false);
        onClose && onClose();
    };

    /** On delta change  */
    const onChangeDelta = () => setDelta(!delta);

    /** Convert file to blob - original approach */
    const convertToBlob = (file) => {
        return new Promise((resolve, reject) => {
            const fileReader = new FileReader();
            fileReader.readAsDataURL(file);
            fileReader.onload = () => resolve(fileReader.result);
            fileReader.onerror = (error) => reject(error);
        });
    };

    /** Handle file input change - original approach */
    const handleFileInput = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        const blobFile = await convertToBlob(file);
        setFileData((prevState) => ({
            ...prevState,
            name: file.name,
            blob_path: blobFile
        }));
    };

    const triggerFileBtnClick = () => {
    const fileInput = document.getElementById("direct-file-input");

    if (!fileInput) {
        logToSentry("Direct file input element not found");
        return;
    }

    fileInput.click();
};

    /** Upload file - original approach */
    const handleDirectUpload = async () => {
        if (!fileData.name || !fileData.blob_path) {
            triggerFileBtnClick();
            return;
        }
        try {
            setUploading(true);
            const data = { ...fileData, isDelta: delta };
            await dispatch(actions.dataCheck.create.postRequest(data));
            await dispatch(actions.dataCheck.create.getRequest());
            dispatch(actions.toast.create.success(strings("messages.fileUploadSuccess")));
            dispatch(actions.dataCheck.create.clear());
            setFileData({});
            onClose && onClose();
        } catch (error) {
            console.error(error);
            logToSentry("On direct file upload:", { error });
            dispatch(actions.toast.create.error(strings("errors.onDataCheckfileUploadFailure")));
        } finally {
            setUploading(false);
        }
    };

    /** Open CSVBox iframe */
    const openCSVBox = () => {
        if (!fileData.file_type_id) {
            dispatch(
                actions.toast.create.error(
                    strings("messages.pleaseSelectFileType")
                )
            );
            return;
        }
        
        console.log("Opening CSVBox with config:", {
            licenceKey: config.csvBox.licenceKey,
            userId: identityId,
            fileTypeId: fileData.file_type_id,
            isDelta: delta
        });
        
        setCsvBoxOpen(true);
    };

    const { file_type_id } = fileData;
    const isDisabled = !file_type_id;
    
    // Build CSVBox URL with parameters
    const csvBoxUrl = `https://app.csvbox.io/upload/${config.csvBox.licenceKey}?user_id=${identityId || 'default_user'}&file_type_id=${file_type_id || ''}&is_delta=${delta}`;

    return (
        <>
        <FormControl fullWidth>
            {filetypes && filetypes.length > 0 && (
                <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12}>
                        <FormControl
                            fullWidth
                            margin="normal"
                            variant="standard"
                        >
                            <InputLabel>{strings("fileType")}</InputLabel>
                            <Select
                                label={strings("fileType")}
                                onChange={(e) => {
                                    setFileData((prevState) => ({
                                        ...prevState,
                                        file_type_id: e.target.value
                                    }));
                                }}
                                value={file_type_id || ""}
                            >
                                {filetypes.map((item) => (
                                    <MenuItem key={item.id} value={item.id}>
                                        {item.description}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    defaultChecked
                                    onChange={onChangeDelta}
                                />
                            }
                            label={strings("isDelta")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Input
                            type="file"
                            id="direct-file-input"
                            inputRef={fileInputRef}
                            onChange={handleFileInput}
                            inputProps={{ accept: ".csv" }}
                            sx={{ display: "none" }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            variant="outlined"
                            size="medium"
                            color="primary"
                            onClick={triggerFileBtnClick}
                            disabled={isDisabled || uploading}
                            fullWidth
                        >
                            {fileData.name ? fileData.name : "Upload File"}
                        </Button>
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            variant="contained"
                            size="medium"
                            color="primary"
                            onClick={openCSVBox}
                            disabled={isDisabled || uploading}
                            fullWidth
                        >
                            {strings("importwizard")}
                        </Button>
                    </Grid>
                    {fileData?.name && (
                        <Grid item xs={12}>
                            <Button
                                variant="contained"
                                size="medium"
                                color="success"
                                onClick={handleDirectUpload}
                                disabled={uploading}
                                fullWidth
                            >
                                {uploading ? strings("uploading") : strings("uploadFile")}
                            </Button>
                        </Grid>
                    )}
                    {!file_type_id && (
                        <Grid item xs={12}>
                            <Typography variant="caption" color="error" sx={{ display: "block" }}>
                                {strings("pleaseSelectFileType")}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            )}
            <Divider sx={{ mt: 2 }} />
            <DialogActions>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    color="primary"
                >
                    {strings("cancel")}
                </Button>
            </DialogActions>
        </FormControl>
        
        {/* CSVBox iframe modal */}
        <Dialog
            open={csvBoxOpen}
            onClose={() => setCsvBoxOpen(false)}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle>
                {strings("importCSVFile")}
                <Button
                    onClick={() => setCsvBoxOpen(false)}
                    sx={{ float: 'right' }}
                    size="small"
                >
                    {strings("close")}
                </Button>
            </DialogTitle>
            <DialogContent sx={{ height: '80vh', p: 0 }}>
                <iframe
                    src={csvBoxUrl}
                    style={{
                        width: '100%',
                        height: '100%',
                        border: 'none'
                    }}
                    title= {strings("csvboximporter")}
                />
            </DialogContent>
        </Dialog>
    </>
    );
};
